import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Typography,
  Button,
  Container,
  alpha,
  Switch,
  Chip,
} from '@mui/material';
import {
  Check as CheckIcon,
  Rocket as RocketIcon,
  TrendingUp as GrowthIcon,
  Star as StarIcon,
  Business as EnterpriseIcon,
  HeadsetMic as HeadsetIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { tokens, shadows } from '../../theme';
import { RECOMMENDED_TIER_RIBBON } from '../../content/publicClaims';

interface PricingTier {
  name: string;
  price: number | null;
  annualPrice: number | null;
  description: string;
  icon: React.ReactNode;
  gradient: string;
  features: string[];
  cta: string;
  recommended?: boolean;
  consultingHighlight?: string;
}

const pricingTiers: PricingTier[] = [
  {
    name: 'Self-Serve',
    price: 49,
    annualPrice: 39,
    description: 'Perfect for getting started',
    icon: <RocketIcon />,
    gradient: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
    features: ['Up to 1,000 transactions/mo', '3 integrations', 'AI insights from your synced data', 'Email support, no priority'],
    cta: 'Start Free',
  },
  {
    name: 'Guided',
    price: 149,
    annualPrice: 119,
    description: 'For growing businesses',
    icon: <GrowthIcon />,
    gradient: 'linear-gradient(135deg, #EC4899 0%, #F43F5E 100%)',
    recommended: true,
    consultingHighlight: '1hr/quarter consulting',
    features: ['Up to 10,000 transactions/mo', 'Every integration we have built', 'AI insights from your synced data', '1hr quarterly strategy session'],
    cta: 'Start Free',
  },
  {
    name: 'Managed',
    price: 349,
    annualPrice: 279,
    description: 'For data-driven teams',
    icon: <StarIcon />,
    gradient: 'linear-gradient(135deg, #10B981 0%, #06D6A0 100%)',
    consultingHighlight: '2hrs/month consulting',
    features: ['Up to 50,000 transactions/mo', 'A standing call with the person who builds it', '2hrs monthly consulting', 'Priority support'],
    cta: 'Start Free',
  },
  {
    name: 'White Glove',
    price: null,
    annualPrice: null,
    description: 'Full-service partnership',
    icon: <EnterpriseIcon />,
    gradient: 'linear-gradient(135deg, #F59E0B 0%, #FB923C 100%)',
    consultingHighlight: 'Unlimited consulting',
    features: ['No usage limits', 'One person, not a team', 'On-demand consulting', 'Direct line to the person building it'],
    cta: 'Contact Sales',
  },
];

const PricingPreview: React.FC = () => {
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(true);

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        background: tokens.background.gradient,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: '-20%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          height: '100%',
          background: `radial-gradient(ellipse 60% 40% at 50% 0%, ${alpha(tokens.primary.main, 0.08)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0.15, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
            <Typography
              variant="overline"
              sx={{
                color: tokens.primary.main,
                fontWeight: 700,
                letterSpacing: '0.1em',
                mb: 2,
                display: 'block',
              }}
            >
              Simple Pricing
            </Typography>

            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                color: tokens.text.primary,
                mb: 2,
                fontSize: { xs: '2rem', md: '2.75rem' },
                letterSpacing: '-0.03em',
              }}
            >
              Plans that{' '}
              <Box
                component="span"
                sx={{
                  background: tokens.primary.gradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                grow with you
              </Box>
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: tokens.text.secondary,
                maxWidth: 500,
                mx: 'auto',
                mb: 4,
              }}
            >
              Start free, scale as you grow. The free tier does not expire.
            </Typography>

            {/* Billing Toggle */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: isAnnual ? 500 : 600,
                  color: isAnnual ? tokens.text.secondary : tokens.text.primary,
                }}
              >
                Monthly
              </Typography>
              
              <Switch
                checked={isAnnual}
                onChange={(e) => setIsAnnual(e.target.checked)}
                sx={{
                  width: 56,
                  height: 32,
                  padding: 0,
                  '& .MuiSwitch-switchBase': {
                    padding: 0,
                    margin: '4px',
                    '&.Mui-checked': {
                      transform: 'translateX(24px)',
                      color: '#fff',
                      '& + .MuiSwitch-track': {
                        background: tokens.primary.gradient,
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiSwitch-thumb': {
                    width: 24,
                    height: 24,
                    boxShadow: shadows.md,
                  },
                  '& .MuiSwitch-track': {
                    borderRadius: 16,
                    backgroundColor: tokens.border.main,
                    opacity: 1,
                  },
                }}
              />
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{
                    fontWeight: isAnnual ? 600 : 500,
                    color: isAnnual ? tokens.text.primary : tokens.text.secondary,
                  }}
                >
                  Annual
                </Typography>
                
                <AnimatePresence>
                  {isAnnual && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                    >
                      <Chip
                        label="Save 20%"
                        size="small"
                        sx={{
                          height: 24,
                          background: tokens.success.gradient,
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>
            </Box>
          </Box>
        </motion.div>

        {/* Pricing Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 3,
            mb: 6,
          }}
        >
          {pricingTiers.map((tier, index) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0.15, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <Box
                sx={{
                  height: '100%',
                  p: tier.recommended ? '3px' : 0,
                  borderRadius: tier.recommended ? '24px' : '20px',
                  background: tier.recommended ? tier.gradient : 'transparent',
                  position: 'relative',
                }}
              >
                {/* Popular Badge */}
                {tier.recommended && (
                  <Chip
                    label={RECOMMENDED_TIER_RIBBON}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 10,
                      background: tier.gradient,
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      boxShadow: `0 4px 14px ${alpha(tokens.secondary.main, 0.4)}`,
                    }}
                  />
                )}

                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    background: tokens.background.paper,
                    borderRadius: '20px',
                    border: tier.recommended ? 'none' : `1px solid ${tokens.border.light}`,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: shadows.xl,
                    },
                  }}
                >
                  {/* Card Header */}
                  <Box sx={{ p: 3, pb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: tier.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        mb: 2,
                        boxShadow: `0 8px 20px ${alpha(tokens.primary.main, 0.25)}`,
                      }}
                    >
                      {React.cloneElement(tier.icon as React.ReactElement, { sx: { fontSize: 24 } })}
                    </Box>

                    <Typography variant="h6" sx={{ fontWeight: 700, color: tokens.text.primary }}>
                      {tier.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                      {tier.description}
                    </Typography>

                    {tier.consultingHighlight && (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          mt: 1,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: '8px',
                          background: alpha('#10B981', 0.1),
                          border: `1px solid ${alpha('#10B981', 0.2)}`,
                        }}
                      >
                        <HeadsetIcon sx={{ fontSize: 12, color: '#10B981' }} />
                        <Typography
                          variant="caption"
                          sx={{ color: '#10B981', fontWeight: 600, fontSize: '0.65rem' }}
                        >
                          {tier.consultingHighlight}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Price */}
                  <Box sx={{ px: 3, pb: 2 }}>
                    {tier.price ? (
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography
                          variant="h3"
                          sx={{ fontWeight: 800, color: tokens.text.primary, fontSize: '2.5rem' }}
                        >
                          ${isAnnual ? tier.annualPrice : tier.price}
                        </Typography>
                        <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                          /mo
                        </Typography>
                      </Box>
                    ) : (
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 800,
                          background: tier.gradient,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        Custom
                      </Typography>
                    )}
                  </Box>

                  {/* Features */}
                  <Box sx={{ flex: 1, px: 3, py: 2 }}>
                    {tier.features.map((feature, idx) => (
                      <Box
                        key={idx}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}
                      >
                        <CheckIcon sx={{ fontSize: 16, color: tokens.success.main }} />
                        <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* CTA */}
                  <Box sx={{ p: 3, pt: 2 }}>
                    <Button
                      fullWidth
                      variant={tier.recommended ? 'contained' : 'outlined'}
                      onClick={() => navigate(tier.price ? '/signup' : '/contact')}
                      sx={{
                        py: 1.25,
                        fontWeight: 600,
                        borderRadius: '10px',
                        ...(tier.recommended
                          ? {
                              background: tier.gradient,
                              boxShadow: `0 8px 20px ${alpha(tokens.secondary.main, 0.35)}`,
                              '&:hover': {
                                background: tier.gradient,
                                filter: 'brightness(1.1)',
                              },
                            }
                          : {
                              borderColor: tokens.border.main,
                              color: tokens.text.primary,
                              '&:hover': {
                                borderColor: tokens.primary.main,
                              },
                            }),
                      }}
                    >
                      {tier.cta}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </motion.div>
          ))}
        </Box>

        {/* View Full Pricing Link */}
        <motion.div
          initial={{ opacity: 0.15 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="text"
              onClick={() => navigate('/pricing')}
              endIcon={<ArrowIcon />}
              sx={{
                color: tokens.primary.main,
                fontWeight: 600,
                '&:hover': {
                  background: alpha(tokens.primary.main, 0.08),
                },
              }}
            >
              View Full Pricing & Feature Comparison
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default PricingPreview;
